import { create } from "zustand";
import { Timestamp } from "firebase/firestore";
import type { ActivityActor } from "src/lib/activityService";
import type { CarouselPublicationListItem } from "src/types/carouselPublication";
import {
  deleteCarouselPublication,
  listCarouselPublications,
  publishHappeningNowCarousel,
  republishCarouselToDraft,
  setCarouselPublicationStarred,
  type RepublishOptions,
  type RepublishResult,
} from "src/lib/carouselPublicationService";
import { Constants } from "src/constant";

export type CarouselPublicationsStore = {
  publications: CarouselPublicationListItem[];
  loading: boolean;
  error: string | null;
  /** When true, list is filtered to starred-only (toggle in panel header). */
  starredOnly: boolean;

  load: () => Promise<void>;
  toggleStarredOnly: (next?: boolean) => Promise<void>;

  publish: (
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<{ publicationId: string; cardCount: number }>;

  republishToDraft: (
    publicationId: string,
    options: RepublishOptions,
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<RepublishResult>;

  setStarred: (
    publicationId: string,
    starred: boolean,
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  remove: (publicationId: string, actor: ActivityActor | null) => Promise<void>;

  reset: () => void;
};

const initialState: Pick<
  CarouselPublicationsStore,
  "publications" | "loading" | "error" | "starredOnly"
> = {
  publications: [],
  loading: false,
  error: null,
  starredOnly: false,
};

export const useCarouselPublicationsStore = create<CarouselPublicationsStore>(
  (set, get) => ({
    ...initialState,

    load: async () => {
      const { starredOnly } = get();
      set({ loading: true, error: null });
      try {
        const rows = await listCarouselPublications({
          sinceDays: starredOnly
            ? undefined
            : Constants.CAROUSEL_HISTORY_DEFAULT_DAYS,
          starredOnly,
        });
        set({ publications: rows, loading: false });
      } catch (e: unknown) {
        set({
          error: e instanceof Error ? e.message : "Failed to load history",
          loading: false,
        });
      }
    },

    toggleStarredOnly: async (next) => {
      const value = next ?? !get().starredOnly;
      set({ starredOnly: value });
      await get().load();
    },

    publish: async (uid, actor) => {
      const result = await publishHappeningNowCarousel(uid, actor);
      // Optimistic prepend so the new publication shows immediately while
      // we re-fetch (server timestamps need a round trip to materialize).
      await get().load();
      return result;
    },

    republishToDraft: async (publicationId, options, uid, actor) => {
      return republishCarouselToDraft(publicationId, options, uid, actor);
    },

    setStarred: async (publicationId, starred, uid, actor) => {
      // Optimistic update so the UI snaps immediately.
      const prev = get().publications;
      set({
        publications: prev.map((p) =>
          p.id === publicationId
            ? {
                ...p,
                starredAt: starred ? Timestamp.now() : null,
                starredBy: starred ? uid : null,
                starredByDisplayName: starred
                  ? actor?.displayName?.trim() || actor?.email || uid
                  : null,
              }
            : p,
        ),
      });
      try {
        await setCarouselPublicationStarred(publicationId, starred, uid, actor);
      } catch (e: unknown) {
        set({
          publications: prev,
          error: e instanceof Error ? e.message : "Could not update star",
        });
        throw e;
      }
    },

    remove: async (publicationId, actor) => {
      await deleteCarouselPublication(publicationId, actor);
      set((s) => ({
        publications: s.publications.filter((p) => p.id !== publicationId),
      }));
    },

    reset: () => set(initialState),
  }),
);
